import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import VideoPlayer from './components/VideoPlayer';
import EmbeddedVideoView from './components/EmbeddedVideoView';
import CloudflareStreamPlayer from './components/CloudflareStreamPlayer';
import TextLessonView from './components/TextLessonView';
import PdfLessonView from './components/PdfLessonView';
import ExternalLessonView from './components/ExternalLessonView';
import LiveLessonView from './components/LiveLessonView';
import LessonMetadata from './components/LessonMetadata';
import LessonNavigation from './components/LessonNavigation';
import CompletionCelebration from './components/CompletionCelebration';
import QuizTaker from '../../components/QuizTaker';
import NesoGuideChat from '../../components/AppChatWidgets/NesoGuideChat';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { authHelpers } from '../../firebaseClient';
import { getCourse } from '../../utils/courseHelpers';
import { isEmbeddableVideoUrl } from '../../utils/videoEmbed';
import {
  getUserEnrollment,
  toggleLessonCompletion,
  touchEnrollmentActivity,
  submitModuleQuiz,
  isCapstoneUnlocked,
  areAllLessonsComplete,
} from '../../utils/enrollmentHelpers';
import { useLearningSession } from '../../hooks/useLearningSession';

const VideoLearningInterface = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgId = searchParams?.get('orgId');
  const courseId = searchParams?.get('courseId');
  const itemIdParam = searchParams?.get('lessonId');

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false); // session-only, not persisted
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !courseId) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      const { session } = await authHelpers.getSession();
      if (!session) {
        navigate('/authentication-portal', { replace: true });
        return;
      }
      setCurrentUser(session.user);

      const { course: courseData } = await getCourse(orgId, courseId);
      if (!courseData) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      const { enrollment: enrollmentData } = await getUserEnrollment(session.user.uid, courseId);
      if (!enrollmentData) {
        navigate(`/course-preview?orgId=${orgId}&courseId=${courseId}`, { replace: true });
        return;
      }

      setCourse(courseData);
      setEnrollment(enrollmentData);
      setLoading(false);

      touchEnrollmentActivity(session.user.uid, courseId).catch(() => {});
    };

    load();
  }, [orgId, courseId, navigate]);

  // Flattens modules into a single ordered list of items — lessons, plus a quiz
  // item right after a module's lessons if that module has one.
  const flatItems = useMemo(() => {
    const flat = [];
    course?.modules?.forEach((module) => {
      module?.lessons?.forEach((lesson) => {
        flat.push({ ...lesson, itemType: 'lesson', moduleTitle: module?.title, moduleId: module?.id });
      });
      if (module?.quiz) {
        flat.push({
          ...module.quiz,
          itemType: 'quiz',
          moduleTitle: module?.title,
          moduleId: module?.id,
        });
      }
    });
    return flat;
  }, [course]);

  const completedLessonIds = enrollment?.completedLessonIds || [];

  const currentItem = useMemo(() => {
    if (itemIdParam) {
      const found = flatItems?.find((i) => i?.id === itemIdParam);
      if (found) return found;
    }
    return flatItems?.[0] || null;
  }, [flatItems, itemIdParam]);

    const currentFlatIndex = flatItems?.findIndex((i) => i?.id === currentItem?.id);
  const isCompleted = currentItem?.itemType === 'lesson' && completedLessonIds?.includes(currentItem?.id);
  const totalItems = flatItems?.length || 0;

  // Starts a learning session on mount, heartbeats every 30s, and ends it on
  // unmount or when the learner navigates to a different lesson (currentItem?.id
  // changing tears down the old session and starts a fresh one).
  useLearningSession({
    orgId,
    userId: currentUser?.uid,
    courseId,
    lessonId: currentItem?.id,
  });

  useEffect(() => {
    setCurrentTime(0);
    setIsPlaying(false);
    setIsBookmarked(false);
  }, [currentItem?.id]);

  const durationSeconds = (currentItem?.duration || 0) * 60;
  const isEmbeddedVideo = currentItem?.itemType === 'lesson' && currentItem?.type === 'video' && isEmbeddableVideoUrl(currentItem?.videoUrl);

  const handleItemChange = (item) => {
    navigate(`/video-learning-interface?orgId=${orgId}&courseId=${courseId}&lessonId=${item?.id}`);
  };

  const handlePrevious = () => {
    if (currentFlatIndex > 0) handleItemChange(flatItems?.[currentFlatIndex - 1]);
  };

  const handleNext = () => {
    if (currentFlatIndex < totalItems - 1) handleItemChange(flatItems?.[currentFlatIndex + 1]);
  };

  const handleMarkComplete = async () => {
    const { enrollment: updated } = await toggleLessonCompletion({
      userId: currentUser?.uid,
      courseId,
      lessonId: currentItem?.id,
      lessonsTotal: flatItems?.filter((i) => i?.itemType === 'lesson')?.length,
      markComplete: !isCompleted,
    });

    if (updated) {
      setEnrollment(updated);
      if (!isCompleted) setShowCelebration(true);
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (!isCompleted && currentTime >= durationSeconds * 0.8) {
      handleMarkComplete();
    }
  };

  const handleQuizSubmit = async (answers) => {
    const { result, error } = await submitModuleQuiz({
      userId: currentUser?.uid,
      courseId,
      moduleId: currentItem?.moduleId,
      quiz: currentItem,
      answers,
      allowRetake: areAllLessonsComplete(course, enrollment),
    });

    if (result) {
      const { enrollment: refreshed } = await getUserEnrollment(currentUser?.uid, courseId);
      if (refreshed) setEnrollment(refreshed);
    }
    return { result, error };
  };

  const handleNextLesson = () => {
    setShowCelebration(false);
    handleNext();
  };

  const handleViewCertificate = () => {
    setShowCelebration(false);
    navigate(`/course-capstone?orgId=${orgId}&courseId=${courseId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading lesson...</span>
        </div>
      </div>
    );
  }

  if (accessDenied || !currentItem) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <Icon name="SearchX" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Lesson not available</h1>
          <p className="text-muted-foreground mb-6">This course has no content yet, or the link is invalid.</p>
          <Link to="/course-catalog">
            <Button variant="default">Back to Catalog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const nextFlatItem = currentFlatIndex < totalItems - 1 ? flatItems?.[currentFlatIndex + 1] : null;
  const isLastItem = currentFlatIndex === totalItems - 1;
  const capstoneUnlocked = course?.capstone ? isCapstoneUnlocked(course, enrollment) : false;

  const renderItemBody = () => {
    if (currentItem?.itemType === 'quiz') {
      const savedResult = enrollment?.moduleQuizResults?.[currentItem?.moduleId];
      return (
        <QuizTaker
          title={currentItem?.title}
          questions={currentItem?.questions}
          passingScore={currentItem?.passingScore}
          onSubmit={handleQuizSubmit}
          lastResult={
            savedResult
              ? { score: savedResult?.bestScore, passed: savedResult?.passed, answers: savedResult?.answers, pointsAwarded: 0 }
              : null
          }
          allowRetake={areAllLessonsComplete(course, enrollment)}
          submitLabel="Submit Quiz"
        />
      );
    }

    switch (currentItem?.type) {
      case 'video':
        if (currentItem?.cloudflareUid) {
          return (
            <CloudflareStreamPlayer
              uid={currentItem?.cloudflareUid}
              customerCode={currentItem?.cloudflareCustomerCode}
              title={currentItem?.title}
            />
          );
        }
        if (isEmbeddedVideo) return <EmbeddedVideoView videoUrl={currentItem?.videoUrl} />;
        return (
          <VideoPlayer
            videoUrl={currentItem?.videoUrl}
            title={currentItem?.title}
            duration={durationSeconds}
            currentTime={currentTime}
            onTimeUpdate={setCurrentTime}
            onEnded={handleVideoEnded}
            isPlaying={isPlaying}
            onPlayPause={() => setIsPlaying((p) => !p)}
          />
        );
      case 'text':
        return <TextLessonView lesson={currentItem} />;
      case 'pdf':
        return <PdfLessonView lesson={currentItem} />;
      case 'external':
        return <ExternalLessonView lesson={currentItem} />;
            case 'live':
        return <LiveLessonView lesson={currentItem} orgId={orgId} courseId={courseId} />;
      default:
        return (
          <div className="bg-card rounded-lg p-12 text-center text-muted-foreground text-sm">
            Unsupported lesson type.
          </div>
        );
    }
  };

  const canMarkComplete =
    currentItem?.itemType === 'lesson' && currentItem?.type === 'video' && !isEmbeddedVideo
      ? currentTime >= durationSeconds * 0.8 || isCompleted
      : true;

  const CapstoneCta = () =>
    course?.capstone ? (
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon name="Trophy" size={18} className="text-accent" />
          <span className="text-sm font-medium text-foreground">
            {capstoneUnlocked ? 'Capstone exam unlocked' : 'Score 60%+ average on module quizzes to unlock the capstone'}
          </span>
        </div>
        <Link to={`/course-capstone?orgId=${orgId}&courseId=${courseId}`}>
          <Button variant="default" size="sm" iconName="ArrowRight" iconPosition="right">
            View Capstone
          </Button>
        </Link>
      </div>
    ) : null;

  const modulesForNav = course?.modules?.map((m) => ({
    ...m,
    lessons: [
      ...(m?.lessons?.map((l) => ({ ...l, completed: completedLessonIds?.includes(l?.id) })) || []),
      ...(m?.quiz
        ? [
            {
              id: m.quiz.id,
              title: m.quiz.title,
              type: 'quiz',
              completed: !!enrollment?.moduleQuizResults?.[m?.id]?.passed,
            },
          ]
        : []),
    ],
  }));

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>
          {currentItem?.title} - {course?.title} - Nesolearn
        </title>
      </Helmet>

      <div className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link
            to={`/course-preview?orgId=${orgId}&courseId=${courseId}`}
            className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent transition-smooth"
          >
            <Icon name="X" size={18} />
            <span className="truncate max-w-[60vw] sm:max-w-xs">{course?.title}</span>
          </Link>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-muted-foreground mr-1">
              {currentFlatIndex + 1} of {totalItems}
            </span>
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted"
              aria-label="Course content"
            >
              <Icon name="ListOrdered" size={18} />
            </button>
            <button
              onClick={() => setMobileGuideOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted"
              aria-label="Neso Guide"
            >
              <Icon name="Sparkles" size={18} className="text-accent" />
            </button>
          </div>
        </div>
      </div>

      {/* Three-column Learning Environment: course outline / content / Neso Guide,
          collapsing to slide-over drawers on mobile via the header's trigger buttons */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)_320px] lg:gap-5 lg:items-start">
          <aside className="hidden lg:block sticky top-20">
            <LessonNavigation
              currentLesson={currentItem}
              modules={modulesForNav}
              onLessonChange={handleItemChange}
              onPrevious={handlePrevious}
              onNext={handleNext}
              hasPrevious={currentFlatIndex > 0}
              hasNext={!isLastItem}
              totalLessons={totalItems}
              completedCount={completedLessonIds?.length || 0}
            />
          </aside>

          <div className="space-y-4 min-w-0">
            {renderItemBody()}

            <div className="flex items-center justify-between bg-white/70 backdrop-blur-xl border border-white/40 rounded-xl p-3">
              <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentFlatIndex === 0} iconName="ChevronLeft" iconPosition="left">
                Previous
              </Button>
              <span className="text-sm font-medium text-muted-foreground">
                {currentFlatIndex + 1} of {totalItems}
              </span>
              <Button variant="outline" size="sm" onClick={handleNext} disabled={isLastItem} iconName="ChevronRight" iconPosition="right">
                Next
              </Button>
            </div>

            {isLastItem && <CapstoneCta />}

            {currentItem?.itemType === 'lesson' && (
              <LessonMetadata
                lesson={currentItem}
                moduleTitle={currentItem?.moduleTitle}
                isCompleted={isCompleted}
                onMarkComplete={handleMarkComplete}
                canMarkComplete={canMarkComplete}
                onBookmark={() => setIsBookmarked((b) => !b)}
                isBookmarked={isBookmarked}
              />
            )}

            {/* Mobile-only: Learning Path/Today's Focus users land here from a course
                link — the outline lives in the drawer, not inline, to keep this
                column focused on the current lesson. */}
          </div>

          <aside className="hidden lg:block sticky top-20 h-[calc(100vh-6rem)]">
            <NesoGuideChat
              courseTitle={course?.title}
              lessonTitle={currentItem?.title}
              lessonExcerpt={currentItem?.itemType === 'lesson' && currentItem?.type === 'text' ? currentItem?.content : ''}
            />
          </aside>
        </div>
      </div>

      {/* Mobile drawers */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex" onClick={() => setMobileSidebarOpen(false)}>
          <div className="w-[85vw] max-w-sm h-full bg-background p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Course Content</h3>
              <button onClick={() => setMobileSidebarOpen(false)} className="p-1.5 rounded hover:bg-muted">
                <Icon name="X" size={18} />
              </button>
            </div>
            <LessonNavigation
              currentLesson={currentItem}
              modules={modulesForNav}
              onLessonChange={(item) => { handleItemChange(item); setMobileSidebarOpen(false); }}
              onPrevious={handlePrevious}
              onNext={handleNext}
              hasPrevious={currentFlatIndex > 0}
              hasNext={!isLastItem}
              totalLessons={totalItems}
              completedCount={completedLessonIds?.length || 0}
            />
          </div>
          <div className="flex-1 bg-black/40 backdrop-blur-sm" />
        </div>
      )}

      {mobileGuideOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex justify-end" onClick={() => setMobileGuideOpen(false)}>
          <div className="flex-1 bg-black/40 backdrop-blur-sm" />
          <div className="w-[90vw] max-w-sm h-full p-3" onClick={(e) => e.stopPropagation()}>
            <NesoGuideChat
              courseTitle={course?.title}
              lessonTitle={currentItem?.title}
              lessonExcerpt={currentItem?.itemType === 'lesson' && currentItem?.type === 'text' ? currentItem?.content : ''}
              onClose={() => setMobileGuideOpen(false)}
            />
          </div>
        </div>
      )}

      <CompletionCelebration
        isVisible={showCelebration}
        onClose={() => setShowCelebration(false)}
        lessonTitle={currentItem?.title || 'this lesson'}
        nextItem={nextFlatItem}
        onNextItem={handleNextLesson}
        onViewCertificate={handleViewCertificate}
        courseProgress={enrollment?.progressPercent || 0}
      />
    </div>
  );
};

export default VideoLearningInterface;